import { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
  Box, TextField, Button, Typography, CircularProgress,
  InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import logo from '../assets/logo.png';
import torahBg from '../assets/torah_bg.jpg';

const LoginPage = () => {
  const [username, setUsername]   = useState('');
  const [password, setPassword]   = useState('');
  const [showPwd, setShowPwd]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const login  = useLogin();
  const notify = useNotify();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    try {
      await login({ username: username.trim(), password });
    } catch (err) {
      notify(err?.message || 'שם משתמש או סיסמא שגויים', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: `url(${torahBg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(135deg, rgba(13,29,62,0.72) 0%, rgba(90,55,20,0.55) 50%, rgba(13,29,62,0.68) 100%)',
        backdropFilter: 'blur(1.5px)',
      },
      position: 'relative',
    }}>
      <Box
        className="crm-login-form"
        component="form"
        onSubmit={handleSubmit}
        dir="rtl"
        sx={{
          position: 'relative',
          zIndex: 1,
          width: 380,
          backgroundColor: '#fff',
          borderRadius: 3,
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{
          backgroundColor: '#0d1d3e',
          px: 4, py: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}>
          <img src={logo} alt="פאר הקולמוס" style={{ width: 160 }} />
          <Typography variant="body2" sx={{ color: 'rgba(200,169,110,0.8)', fontSize: 13 }}>
            מערכת ניהול
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ px: 4, py: 4 }}>
          <Typography variant="h6" fontWeight={700} color="#0d1d3e" mb={3} textAlign="center">
            כניסה למנהלים
          </Typography>

          <TextField
            label="שם משתמש"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            autoComplete="username"
            autoFocus
            sx={{ mb: 2 }}
            InputProps={{ sx: { direction: 'rtl' } }}
          />

          <TextField
            label="סיסמא"
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            autoComplete="current-password"
            sx={{ mb: 3 }}
            InputProps={{
              sx: { direction: 'rtl' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwd(v => !v)} edge="end" size="small" aria-label={showPwd ? 'הסתר סיסמה' : 'הצג סיסמה'}>
                    {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !username.trim() || !password}
            sx={{
              backgroundColor: '#0d1d3e',
              py: 1.4,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 15,
              '&:hover': { backgroundColor: '#162d5a' },
              '&.Mui-disabled': { backgroundColor: 'rgba(13,29,62,0.3)' },
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'כניסה'}
          </Button>
        </Box>

        {/* Footer */}
        <Box sx={{ backgroundColor: '#f8f5f0', py: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            פאר הקולמוס &copy; {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
